import React, { useState, useEffect } from "react";
import { useAuth } from "../services/use-auth";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import * as ROUTES from "../constants/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "../components/Button";

import AuthLayout from "./AuthLayout";
import { FormCard, AuthTitle, AuthSubtitle, ErrorText } from "./authStyles";
import PasswordRequirementsChecklist from "./PasswordRequirementsChecklist";
import {
  evaluatePasswordRequirements,
  firstPasswordRequirementMessage,
} from "./passwordRequirements";

const PasswordResetComponent = () => {
  const authHook = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [errorFeedback, setErrorFeedback] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isValidCode, setIsValidCode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [processing, setProcessing] = useState(false);

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false,
  });

  const oobCode = searchParams.get("oobCode");
  const mode = searchParams.get("mode");

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode || mode !== "resetPassword") {
        setErrorFeedback("Invalid or missing password reset code.");
        setIsLoading(false);
        return;
      }

      try {
        const email = await verifyPasswordResetCode(authHook.auth, oobCode);
        setUserEmail(email);
        setIsValidCode(true);
        setErrorFeedback("");
      } catch (error) {
        console.error("Error verifying password reset code:", error);
        setErrorFeedback("Invalid or expired password reset code.");
      } finally {
        setIsLoading(false);
      }
    };

    verifyCode();
  }, [oobCode, mode, authHook.auth]);

  const validatePassword = (pass) =>
    firstPasswordRequirementMessage(evaluatePasswordRequirements(pass));

  const updatePasswordRequirements = (newPassword, newConfirmPassword) => {
    setPasswordRequirements(
      evaluatePasswordRequirements(newPassword, newConfirmPassword),
    );
  };

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));
    updatePasswordRequirements(newPassword, confirmPassword);
    if (errorFeedback) setErrorFeedback("");
  };

  const handleConfirmPasswordChange = (event) => {
    const newConfirmPassword = event.target.value;
    setConfirmPassword(newConfirmPassword);
    updatePasswordRequirements(password, newConfirmPassword);
    if (errorFeedback) setErrorFeedback("");

    if (newConfirmPassword !== password) {
      setConfirmError("Passwords do not match");
    } else {
      setConfirmError("");
    }
  };

  const handleSubmit = async () => {
    setErrorFeedback("");

    const passError = validatePassword(password);
    if (passError) {
      setPasswordError(passError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match");
      return;
    }

    setProcessing(true);
    try {
      await confirmPasswordReset(authHook.auth, oobCode, password);
      setSuccessMessage("Password reset successful! Redirecting to sign in...");

      setTimeout(() => {
        navigate(ROUTES.SIGN_IN);
      }, 2000);
    } catch (error) {
      console.error("Error resetting password:", error);
      setErrorFeedback("Failed to reset password. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      isValidCode &&
      !passwordError &&
      !confirmError &&
      password &&
      confirmPassword
    ) {
      event.preventDefault();
      handleSubmit();
    }
  };

  if (isLoading) {
    return (
      <AuthLayout>
        <FormCard>
          <AuthTitle theme={theme}>Loading...</AuthTitle>
        </FormCard>
      </AuthLayout>
    );
  }

  if (!isValidCode) {
    return (
      <AuthLayout>
        <FormCard>
          <AuthTitle theme={theme}>Invalid Reset Link</AuthTitle>
          <AuthSubtitle theme={theme}>
            This password reset link is invalid or has expired. Please request a
            new password reset.
          </AuthSubtitle>
          <Button variant="contained" onClick={() => navigate(ROUTES.SIGN_IN)}>
            Go to Sign In
          </Button>
        </FormCard>
      </AuthLayout>
    );
  }

  if (successMessage) {
    return (
      <AuthLayout>
        <FormCard>
          <AuthTitle theme={theme}>Success!</AuthTitle>
          <AuthSubtitle theme={theme}>{successMessage}</AuthSubtitle>
        </FormCard>
      </AuthLayout>
    );
  }

  const isFormValid =
    password &&
    confirmPassword &&
    !passwordError &&
    !confirmError &&
    passwordRequirements.minLength &&
    passwordRequirements.hasNumber &&
    passwordRequirements.hasSymbol &&
    passwordRequirements.passwordsMatch;

  return (
    <AuthLayout>
      <FormCard>
        <AuthTitle theme={theme}>Reset Your Password</AuthTitle>
        <AuthSubtitle theme={theme}>
          Enter a new password for {userEmail}
        </AuthSubtitle>

        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pb: 3,
            width: "100%",
          }}
          noValidate
          autoComplete="off"
          onKeyDown={handleKeyDown}>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            name="password"
            variant="outlined"
            color={theme.palette.mode === "light" ? "secondary" : "primary"}
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
          />

          {(password.length > 0 || confirmPassword.length > 0) && (
            <PasswordRequirementsChecklist requirements={passwordRequirements} />
          )}

          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            variant="outlined"
            color={theme.palette.mode === "light" ? "secondary" : "primary"}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!confirmError}
            helperText={confirmError}
          />
        </Box>

        <Button
          disabled={!isFormValid}
          variant="contained"
          onClick={handleSubmit}
          processing={processing}
          sx={{ mb: 2 }}>
          Reset Password
        </Button>

        <ErrorText>{errorFeedback}</ErrorText>

        <Button variant="text" onClick={() => navigate(ROUTES.SIGN_IN)}>
          Back to Sign In
        </Button>
      </FormCard>
    </AuthLayout>
  );
};

const PasswordResetPage = () => {
  return <PasswordResetComponent />;
};

export default PasswordResetPage;
